import React, { useEffect, useState } from "react";
import newInvestingAccount from "../../Public/assets/investing/newInvestingAccount.svg";
// src / Public / assets / investing / onBoarding.svg;
import onBoarding from "../../Public/assets/investing/onBoarding.svg";
// import src/Public/assets/investing/underReview.svg
import underReview from "../../Public/assets/investing/underReview.svg";
import noFound from "../../Public/assets/investing/noFound.svg";
import { useSettings } from "../../Context/AccountSettings/index";
import { getSession } from "../../axios.config";
import {
  generateInvestingSsoToken,
  getFundAccountSummary,
  getInvestingBannerInfo,
  WalletAccount,
  getBeneficiaryDetails,
} from "../../api";
import { useOverview } from "./../../hooks";

const cardDataJson = {
  // width prop to adjust width of the banner
  noInvestingAccount: {
    imgSrc: newInvestingAccount,
    altText: "No Investing Account Background",
    subTitle: "Open your",
    mainTitle: "Borderless Investment Account",
    bannerDescription: `Increase your wealth by diversifying into Stocks, ETFs, and Stacks.`,
    buttonText: "Get it now",
    width: "w-[334px]",
  },
  onBoarding: {
    imgSrc: onBoarding,
    altText: "Onboarding Background",
    mainTitle:
      "Complete Your <br /> Borderless Investment A/c <br />Onboarding!",
    bannerDescription:
      "You're just a few steps away from completing your onboarding process.",
    buttonText: "Complete Onboarding",
    width: "w-[340px]",
  },
  waitingForApproval: {
    imgSrc: underReview,
    altText: "Application Under Review",
    mainTitle: "Your Application is Under Review",
    bannerDescription:
      "Thanks for completing your onboarding! We're <br/> reviewing your application and will notify youl once it's approved.",
    buttonText: "Go to My Account",
    width: "w-[354px]",
  },
  noFound: {
    imgSrc: noFound,
    altText: "No Found Background",
    mainTitle: "Fund Your Account",
    bannerDescription:
      "Looks like your Borderless <br /> Investment account is empty!",
    buttonText: "Fund Now ",
    width: "w-[280px]",
  },
};

const InvestingBannerSkeleton = () => {
  return (
    <div
      className="absolute sm:right-0 left-1/2 sm:left-auto transform -translate-x-1/2 sm:transform-none z-50 mt-2 bg-white border border-gray-200 rounded-[24px] shadow-lg w-[350px] top-[25px] p-[28px] flex flex-col justify-center items-center gap-5"
      style={{ left: window.innerWidth < 640 ? "-36px" : undefined }}
    >
      <div className="w-[120px] h-[80px] bg-greyShade rounded mb-4 blink shadow-sm"></div>
      <div className="flex flex-col items-center w-full gap-2 text-center">
        <div className="w-[100px] h-[20px] bg-greyShade rounded mb-2 blink shadow-sm"></div>
        <div className="w-[180px] h-[28px] bg-greyShade rounded mb-2 blink shadow-sm"></div>
        <div className="w-[160px] h-[20px] bg-greyShade rounded blink shadow-sm"></div>
      </div>
      <div className="rounded-[8px] px-8 py-2.5 bg-greyShade w-[130px] h-[40px] mt-4 blink"></div>
    </div>
  );
};

const CommonCard = ({
  data,
  handleRedirectAction,
  buttonLoading,
  AccountData,
  error = false,
}) => {
  return (
    <div
      className={`absolute sm:right-0 left-1/2 sm:left-auto transform -translate-x-1/2 sm:transform-none z-50 mt-2 bg-white border border-gray-200 rounded-[24px] shadow-lg ${
        data?.width || "w-[350px]"
      } top-[25px] p-[28px] flex flex-col justify-center items-center gap-5`}
      style={{ left: window.innerWidth < 640 ? "-36px" : undefined }}
    >
      <div>
        <img src={data?.imgSrc} alt={data?.altText} />
      </div>
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        {data?.subTitle && (
          <div className="font-[geomanistbook] font-normal text-base leading-6 tracking-normal text-[#767c8f]">
            {data.subTitle}
          </div>
        )}
        <div
          className="font-[geomanistbook] font-normal text-[20px] leading-[28px] tracking-normal"
          dangerouslySetInnerHTML={{ __html: data?.mainTitle }}
        />
        <div
          className="font-[geomanistRegular] font-normal text-sm leading-5 tracking-normal text-[#767c8f]"
          dangerouslySetInnerHTML={{ __html: data?.bannerDescription }}
        />
      </div>
      {AccountData?.data?.status === "APPROVED" && (
        <div
          className="rounded-[8px] px-8 py-2.5 bg-[#0061d3] text-center cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            handleRedirectAction();
          }}
        >
          {buttonLoading ? (
            <span>
              <span className="inline-block w-5 h-5 border-2 border-[#ccc] border-t-[#007bff] rounded-full animate-spin align-middle" />
            </span>
          ) : (
            <span className="text-white font-[geomanistbook] font-normal text-sm leading-5 tracking-normal">
              {data?.buttonText}
            </span>
          )}
        </div>
      )}
      <div>
        {error && (
          <div className=" sm:right-0 left-1/2 sm:left-auto transform -translate-x-1/2 sm:transform-none z-50 mt-2 top-[400px]">
            <span className="bg-red-500 text-white px-3 py-2 rounded-lg text-sm font-[geomanistRegular] text-center block">
              {error}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const BalanceCard = ({
  user,
  buttonLoading,
  investingBannerInfo,
  handleRedirectAction,
  AccountData,
}) => {
  const [balance, setBalance] = useState({});
  const [loading, setLoading] = useState(true);
  const [fundLoading, setFundLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    setIsOpen,
    setCurrentFlow,
    setStateVariable,
    setInvestmentAccount,
    setBeneficiarySelected,
  } = useOverview();

  useEffect(() => {
    const fetchBalanceInfo = async () => {
      if (user?.custId) {
        setLoading(true);

        try {
          const res = await getFundAccountSummary(user?.custId);
          if (res.status === 200) {
            setBalance(res.data.data);
          }
        } catch (e) {
          console.error("Error fetching investing banner info:", e);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchBalanceInfo();
  }, [user?.custId]);

  if (loading) {
    return <InvestingBannerSkeleton />;
  }

  const handleFundAccount = async () => {
    setFundLoading(true);
    try {
      // Check wallet API for wallet amount
      const walletResponse = await WalletAccount();
      const accounts = walletResponse?.data?.accounts || [];

      // Check if any wallet account has balance > 0
      const hasWalletBalance = accounts.some((account) => account?.balance > 0);

      // If wallet amount is not available, show AddFunds flow
      if (!hasWalletBalance) {
        setIsOpen(true);
        setCurrentFlow("AddFunds");
      } else {
        // If wallet amount is present, fetch beneficiary details
        const beneficiaryResponse = await getBeneficiaryDetails([
          "beneficiaryDetails",
          { status: "APPROVED" },
        ]);

        // Get all beneficiaries
        const beneficiaries = beneficiaryResponse?.data?.beneficiaries || [];

        // Find the investment account beneficiary
        const investmentAccount = beneficiaries?.find(
          (bene) => bene?.beneType === "INVESTING"
        );
        
        if (investingBannerInfo?.fundProvider === "DW") {
         return   handleRedirectAction();
        }

        if (investmentAccount) {
          // Set the flow state
          setIsOpen(true);
          setCurrentFlow("SendMoney");
          setStateVariable(2);
          setInvestmentAccount(true);

          // Pass the investment account data
          setBeneficiarySelected(investmentAccount);
        } else {
          setError("Investment beneficiary not found, please contact support.");
        }
      }
    } catch (err) {
      console.error("Failed to fetch wallet or beneficiary details:", err);
    } finally {
      setFundLoading(false);
    }
  };

  if (balance?.accountSummary?.cashAvailableForTrade === 0) {
    return (
      <CommonCard
        data={cardDataJson.noFound}
        handleRedirectAction={handleFundAccount}
        buttonLoading={fundLoading}
        AccountData={AccountData}
        error={error}
      />
    );
  }
  return (
    <div
      className={`absolute sm:right-0 left-1/2 sm:left-auto transform -translate-x-1/2 sm:transform-none z-50 mt-2 bg-white border border-gray-200 rounded-[24px] shadow-lg w-[270px] top-[25px] p-[28px] flex flex-col gap-2`}
      style={{ left: window.innerWidth < 640 ? "-36px" : undefined }}
    >
      <div className="font-[geomanistbook] font-normal text-[14px] leading-[20px] tracking-normal text-[#767C8F]">
        Cash Balance
      </div>
      <div className="font-[geomanistbook] font-normal text-[24px] leading-[32px] tracking-normal text-[#3C404A]">
        ${balance?.accountSummary?.cashAvailableForTrade ?? 0}
      </div>
      <div
        className="rounded-[8px] px-8 py-2.5 bg-[#0061d3] text-center cursor-pointer mt-6 mb-6"
        onClick={(e) => {
          e.stopPropagation();
          handleFundAccount();
        }}
      >
        {buttonLoading ? (
          <span>
            <span className="inline-block w-5 h-5 border-2 border-[#ccc] border-t-[#007bff] rounded-full animate-spin align-middle" />
          </span>
        ) : (
          <span className="text-white font-[geomanistbook] font-normal text-sm leading-5 tracking-normal">
            Add Fund
          </span>
        )}
      </div>
      <div>
        {error && (
          <div className=" sm:right-0 left-1/2 sm:left-auto transform -translate-x-1/2 sm:transform-none z-50 mt-2 top-[400px]">
            <span className="bg-red-500 text-white px-3 py-2 rounded-lg text-sm font-[geomanistRegular] text-center block">
              {error}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const InvestingBanner = React.forwardRef((props, ref) => {
  const { useAccountData } = useSettings();

  const user = JSON.parse(getSession("userId"));

  const { data: AccountData } = useAccountData(
    user?.userId || user?.userData?.userId
  );

  const [investingBannerInfo, setInvestingBannerInfo] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBannerInfo = async () => {
      setLoading(true);
      try {
        if (user?.custId) {
          const res = await getInvestingBannerInfo(user.custId);
          if (res.status === 200) {
            setInvestingBannerInfo(res.data);
          }
        }
      } catch (e) {
        console.error("Error fetching investing banner info:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchBannerInfo();
  }, [user?.custId]);

  const handleRedirectAction = async () => {
    setButtonLoading(true);
    try {
      const response = await generateInvestingSsoToken();
      if (response) {
        const urls = response?.data?.data?.redirectUrl;

        const updatedRedirectUrl = `${urls}/sso-login/${response?.data?.data.authCode}`;

        if (updatedRedirectUrl) {
          window.open(updatedRedirectUrl.trim(), "_blank");
        }
      }
    } catch (error) {
      console.error("Error in redirect action:", error);
    } finally {
      setButtonLoading(false);
    }
  };

  if (loading) {
    return <InvestingBannerSkeleton />;
  }

  return (
    <div ref={ref}>
      {user.custId && investingBannerInfo ? (
        investingBannerInfo?.nextStep !== "COMPLETE" ? (
          <CommonCard
            data={cardDataJson.onBoarding}
            handleRedirectAction={handleRedirectAction}
            buttonLoading={buttonLoading}
            AccountData={AccountData}
          />
        ) : investingBannerInfo?.nextStep === "COMPLETE" &&
          investingBannerInfo.accountStatus !== "APPROVED" ? (
          <CommonCard
            data={cardDataJson.waitingForApproval}
            handleRedirectAction={handleRedirectAction}
            buttonLoading={buttonLoading}
            AccountData={AccountData}
          />
        ) : (
          <BalanceCard
            user={user}
            handleRedirectAction={handleRedirectAction}
            buttonLoading={buttonLoading}
            investingBannerInfo={investingBannerInfo}
            AccountData={AccountData}
          />
        )
      ) : (
        <CommonCard
          data={cardDataJson.noInvestingAccount}
          handleRedirectAction={handleRedirectAction}
          buttonLoading={buttonLoading}
          AccountData={AccountData}
        />
      )}
    </div>
  );
});

export default InvestingBanner;
